import { useQuery } from '@tanstack/react-query';

import { previewWorkflowBundle } from '@/lib/api/generated/api';
import type { BundlePreview } from '@/lib/api/generated/model';

import { workflowKeys } from '../queryKeys';

/**
 * What exporting a workflow would carry, and what stops it (spec 203).
 *
 * Read only while the dialog is open: it walks the workflow's links and lists
 * the workspace's agent and skill folders, which is not worth doing for every
 * row of a list. Never cached across opens — a child published a moment ago
 * must stop blocking the export at once.
 */
export function useBundlePreview(
  workspaceId: string,
  workflowId: string,
  enabled: boolean,
) {
  const query = useQuery({
    queryKey: workflowKeys.bundlePreview(workspaceId, workflowId),
    queryFn: async (): Promise<BundlePreview> => {
      const response = await previewWorkflowBundle(workspaceId, workflowId);
      if (response.status !== 200) {
        throw new Error('What this export would hold could not be loaded.');
      }
      return response.data;
    },
    enabled,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  return {
    preview: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
    refetch: query.refetch,
  };
}
